import { booleanPointInPolygon, point } from '@turf/turf';
import axios from 'axios';
import { inject } from 'inversify';

import { GEOMETRY_TYPE, ZONE_TYPE, ZONE_TYPE_VALUE } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Environment } from '@/services/Environment';
import { Logger } from '@/services/Logger';
import { ZoneStore } from '@/stores/ZoneStore';
import { jwt } from '@/utils/jwt';
import { withPerfSpan, setPerfContext, clearPerfContext } from '@/utils/perfContext';
import { IZone } from '~/IZone';
import { IZoneType } from '~/IZoneType';
import { IGeoPosition } from '~/api/IGeoPosition';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';

interface ZoneProperties {
  type: ZONE_TYPE;
  NAME: string;
  Name?: string;
  KEY: string | null;
  CODE: string;
  COLOR: string;
  FILL_COLOR: string;
  FILL_OPACITY: number;
  STROKE_COLOR: string;
  STROKE_WIDTH: number;
  STROKE_STYLE?: string;
  JOIN_STYLE?: string;
}

interface ZoneGeometry {
  type: string;
  coordinates: number[][][] | number[][][][];
}

interface ZoneFeature {
  type: string;
  properties: ZoneProperties;
  geometry: ZoneGeometry;
}

@SignalrListener
@service(ZoneManager)
export class ZoneManager {
  @inject(Logger) protected logger: Logger;
  @inject(ZoneStore) protected zoneStore: ZoneStore;
  @inject(UserController) protected userController: UserController;
  @inject(Environment) protected environment: Environment;

  protected loaded = false;

  get hasLoaded() {
    return this.loaded;
  }

  get getZones() {
    return this.zoneStore.all();
  }

  reset() {
    this.loaded = false;
    this.zoneStore.set([]);
  }

  resetZones(type: ZONE_TYPE) {
    const zones = this.getZones.filter((zone) => zone.properties.type === type);

    for (const zone of zones) {
      this.zoneStore.remove(zone.properties.code);
    }
  }

  async load(type?: ZONE_TYPE) {
    const label = `Zones: load${type ? ` (${type})` : ''}`;

    //  Set context so long tasks get attributed to Zones
    setPerfContext(label);

    const url = `${this.environment.zoServiceZoneBaseUrl}zones/read/${this.userController.activeUser.tenantKey}?type=${type || ''}`;

    try {
      await withPerfSpan(
        'ui:zones:load',
        async () => {
          const res = await axios.get<{ geoJson?: { url?: string; type?: string }[] }>(url, {
            headers: {
              Authorization: `Bearer ${jwt()}`,
            },
          });

          // The response contains a geoJson array with a URL to fetch the actual zones
          const presignedUrls = res.data.geoJson
            ?.filter((item) => typeof item.url === 'string' &&
          item.url.trim() !== '' &&
          typeof item.type === 'string' &&
          item.type.trim() !== '' &&
          item.type !== ZONE_TYPE.BILLING)
            .map((item) => ({ type: item.type, url: item.url }));
          if (presignedUrls && presignedUrls.length > 0) {
            const geoJsonPromises: Promise<void>[] = [];
            const featureCounts: number[] = [];
            for (const presignedUrl of presignedUrls) {
              if (typeof presignedUrl.url === 'string') {
                geoJsonPromises.push(
                  (async () => {
                    try {
                      const geoJsonRes = await axios.get(presignedUrl.url as string, {
                        responseType: 'json',
                      });
                      const geoJsonData = geoJsonRes.data as { features: ZoneFeature[] };
                      featureCounts.push(geoJsonData.features.length);
                      const zones: IZone[] = geoJsonData.features
                        .map((feature: ZoneFeature) => ({
                          type: feature.type,
                          properties: {
                            type: presignedUrl.type as ZONE_TYPE,
                            name: (feature.properties.NAME || feature.properties.Name) ?? '',
                            key: feature.properties.KEY ?? '',
                            code: `${presignedUrl.type}_${feature.properties.CODE}`,
                            fillColor: feature.properties.FILL_COLOR || feature.properties.COLOR,
                            fillOpacity: feature.properties.FILL_OPACITY,
                            strokeColor: feature.properties.STROKE_COLOR,
                            strokeWidth: feature.properties.STROKE_WIDTH,
                            strokeStyle: feature.properties.STROKE_STYLE,
                            joinStyle: feature.properties.JOIN_STYLE,
                          },
                          geometry: feature.geometry,
                        }));

                      this.updateZones(zones);
                    }
                    catch (geoJsonError) {
                      this.logger.error('Failed to fetch zone data from remote URL');
                    }
                  })(),
                );
              }
            }
            await Promise.all(geoJsonPromises);

            //  Track zone load details beyond the perf span
            const totalZoneFeatures = featureCounts.reduce((sum, c) => sum + c, 0);
            this.logger.track('zones.load_details', {
              zoneType: type ?? 'all',
              urlCount: presignedUrls.length,
              totalFeatures: totalZoneFeatures,
            });
          }
        },
        { zoneType: type ?? 'all' },
        { mark: true, slowMs: 300 },
      );
    }

    catch (error) {
      if (axios.isAxiosError(error) && error.response?.status !== 404) {
        this.logger.error('Failed to load zones', error);
      }
    }
    finally {
      this.loaded = true;

      //  Keep context through next paint so any render longtasks get labeled
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          clearPerfContext();
        });
      });
    }
  }

  updateZones(zones: IZone[]) {
    //  Avoid logging massive zone payloads
    this.logger.info('Setting zones', { count: zones.length });
    this.zoneStore.update(...zones);
  }

  getZoneByPosition(position: IGeoPosition, zoneType: ZONE_TYPE) {
    const latLng = point([position.lng, position.lat]);
    const zones = this.getZones.filter((zone) => zone.properties.type === zoneType);
    const matchedZones: IZone[] = [];
    for (const zone of zones) {
      if (zone.geometry.type === GEOMETRY_TYPE.POLYGON || zone.geometry.type === GEOMETRY_TYPE.MULTIPOLYGON) {
        const polygonFeature: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon,
        { name: string; key: string; code: string }> = {
          type: 'Feature',
          geometry: zone.geometry.type === GEOMETRY_TYPE.POLYGON
            ? {
              type: GEOMETRY_TYPE.POLYGON,
              coordinates: zone.geometry.coordinates as number[][][],
            }
            : {
              type: GEOMETRY_TYPE.MULTIPOLYGON,
              coordinates: zone.geometry.coordinates as number[][][][],
            },
          properties: zone.properties,
        };
        if (booleanPointInPolygon(latLng, polygonFeature)) {
          // Collect all zones the point belongs to
          matchedZones.push(zone);
        }
      }
    }
    if (matchedZones.length === 1) {
      return matchedZones.at(0);
    }
    return undefined;
  }

  getDispatchZone(position: IGeoPosition) {
    return this.getZoneByPosition(position, ZONE_TYPE.DISPATCH);
  }

  getResponseZone(position: IGeoPosition) {
    return this.getZoneByPosition(position, ZONE_TYPE.RESPONSE);
  }

  findZone(code: string) {
    return this.zoneStore.find(code);
  }

  @SignalrMessage('ZoneUpdateMsg')
  async onZoneUpdated(response: IZoneType & IDispatchMessage) {
    if (response.zoneType === ZONE_TYPE_VALUE.DISPATCH as unknown as typeof response.zoneType) {
      this.resetZones(ZONE_TYPE.DISPATCH);
      await this.load(ZONE_TYPE.DISPATCH);
    }
    else {
      this.resetZones(ZONE_TYPE.RESPONSE);
      await this.load(ZONE_TYPE.RESPONSE);
    }
  }
}
